import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Link } from '@/Components/UI/Button/Button';
import Icon from '@/Components/UI/Icon/Icon';
import Modal from '@/Components/UI/Modal/Modal';
import withModal from '../withModalHOC';

import { spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';

import { getBaseUrl } from '@/Utils/commonUtils';
import {
  LINK_SPREADSHEET_LIVESHARING_SUPPORT,
  LINK_SUPPORT_DOCS,
  LIVESHARE,
  MODAL_ADD_VIEW,
  MODAL_CONTACT_SALES,
  MODAL_MAKE_COPY,
  MODAL_PAPERCUT,
  MODAL_REQUIRE_ACCOUNT,
  MODAL_URL_FOR_BI_AND_SPREADSHEETS,
  URL_DATASET,
} from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';

import { hasNoWritePermission } from '@/helpers/spreadsheetHelper';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import {
  createLiveShare,
  fetchLiveSharesEntries,
  removeLiveShare,
} from '@/redux/reducers/spreadsheetSlice';
import clsx from 'clsx';
import ViewRow from './ViewRow';
import ViewToggle from './ViewToggle';

const UrlForBiAndSpreadsheets = ({ show, hideModal, preselectedView = {} }) => {
  const dispatch = useDispatch();
  const currentFile = useCurrentFile();

  const [isLoading, setIsLoading] = useState(false);
  const [isToolTipVisible, setIsToolTipVisible] = useState(false);
  const [selectedView, setSelectedView] = useState('');
  const [isSelectedViewEnabled, setIsSelectedViewEnabled] = useState(false);
  const [selectedViewLiveShare, setSelectedViewLiveShare] = useState('');
  const [inputValue, setInputValue] = useState('');
  const isProcessingRef = useRef(false);

  const { sheetViews, liveSharesEntries } = useSelector((state) => state[spreadsheetSliceName]);
  const { user } = useSelector((state) => state[userDataSliceName]);
  const { isFileShared } = currentFile || {};
  const { canLivesharing } = user?.userProperties || {};

  const uuid = getFileUuidFromPath();

  const getInitialData = async () => {
    setIsLoading(true);
    dispatch(fetchLiveSharesEntries()).then(() => {
      setIsLoading(false);
    });
  };

  const refreshSelectedViewLiveShare = () => {
    setIsLoading(true);
    setSelectedViewLiveShare(
      liveSharesEntries.find(
        (liveShareEntry) => liveShareEntry.ClientStateVersionId === selectedView?.client_state
      )
    );
    setIsLoading(false);
  };

  const checkIfSelectedViewIsEnabled = () => {
    setIsLoading(true);
    const isEnabled = liveSharesEntries.some(
      (liveShareEntry) => liveShareEntry.ClientStateVersionId === selectedView?.client_state
    );
    setIsSelectedViewEnabled(isEnabled);
    setIsLoading(false);
  };

  const _createLiveShare = () => {
    return dispatch(createLiveShare({ selectedView })).finally(() => {
      setIsLoading(false);
    });
  };

  const _removeLiveShare = () => {
    return dispatch(removeLiveShare({ shareId: selectedViewLiveShare?.ShareId })).finally(() => {
      setIsLoading(false);
    });
  };

  const onToggleButtonChange = async () => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setIsLoading(true);

    try {
      if (user?.is_anonymous) {
        dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }));
      } else if (!user?.is_anonymous && isFileShared && hasNoWritePermission(currentFile)) {
        dispatch(showModal({ name: MODAL_MAKE_COPY }));
      } else {
        if (!canLivesharing) {
          dispatch(showModal({ name: MODAL_PAPERCUT }));
          return;
        }

        if (isSelectedViewEnabled) {
          await _removeLiveShare();
        } else {
          await _createLiveShare();
        }
      }
    } finally {
      isProcessingRef.current = false;
      setIsLoading(false);
    }
  };

  const onLearnMoreButtonClick = () => {
    window.open(LINK_SPREADSHEET_LIVESHARING_SUPPORT, '_blank', 'noopener,noreferrer');
  };

  const onCopyButtonClick = () => {
    navigator.clipboard.writeText(inputValue);
    setIsToolTipVisible(true);

    setTimeout(() => {
      setIsToolTipVisible(false);
    }, 2000);
  };

  const onAddViewButtonClick = () => {
    dispatch(showModal({ name: MODAL_ADD_VIEW, props: { refreshViews: getInitialData } }));
  };

  const onSelectViewChange = (view) => {
    const { value } = view;
    setIsLoading(true);
    setSelectedView(sheetViews?.find((e) => e?.id === value));
    setIsLoading(false);
  };

  const modalTitleElement = (
    <div className='flex items-center'>
      <span>Link Data to BI & Spreadsheets</span>
      {!canLivesharing && (
        <span className='ml-2'>
          <Icon size={24} name='rocket-launch' color='#D9B500' />
        </span>
      )}
    </div>
  );

  const viewsSelectOptions = sheetViews.map((view) => ({
    value: view.id,
    label: view.name,
    isEnabled: liveSharesEntries.some(
      (liveShareEntry) => liveShareEntry.ClientStateVersionId === view?.client_state
    ),
  }));

  useEffect(() => {
    canLivesharing && getInitialData();
  }, [canLivesharing]);

  useEffect(() => {
    refreshSelectedViewLiveShare();
  }, [selectedView, liveSharesEntries]);

  useEffect(() => {
    checkIfSelectedViewIsEnabled();
    setInputValue(
      `${getBaseUrl('api')}/${URL_DATASET}/${uuid}/${LIVESHARE}/${selectedViewLiveShare?.ShareId}`
    );
  }, [selectedViewLiveShare]);

  useEffect(() => {
    if (show && preselectedView) {
      onSelectViewChange({ value: preselectedView.id });
    }
  }, [show]);

  return (
    <Modal
      isOpen={show}
      onClose={hideModal}
      titleSize='large'
      size='large'
      title={modalTitleElement}
      modalTitleClasses='!my-0'
      subhead='Generate a unique, unlisted Public URL for up to 100k rows and/or 100 columns'
      subheadCss='!ml-0 !mt-1'
      onQuestionMarkButtonClick={onLearnMoreButtonClick}
      isQuestionMarkButton
    >
      <div className='flex flex-row justify-start p-2 mb-3 text-sm border rounded bg-capri-100 border-capri-700 bg-opacity-24'>
        <div className='pr-2'>
          <Icon name='info' color='#0079B6' />
        </div>
        <div className='h-10'>
          <div className='h-3 text-ui-secondary'>
            For more information or service specific limitations{' '}
            <Link to={LINK_SUPPORT_DOCS} className='underline' size='medium'>
              read our support docs
            </Link>
            .
          </div>
          <div className='h-3 text-ui-secondary'>
            For higher usage limits{' '}
            <Button
              className='!p-0 !m-0 underline !text-capri-900 hover:bg-transparent active:bg-transparent h-2'
              variant='ghost'
              onClick={() => dispatch(showModal({ name: MODAL_CONTACT_SALES }))}
            >
              contact sales
            </Button>
            .
          </div>
        </div>
      </div>

      <hr className='w-full my-1' />
      <div className='py-1 text-sm'>
        <div id='view-button' className='flex w-full gap-2'>
          <div className='w-2/6 overflow-scroll min-h-52 max-h-72'>
            {viewsSelectOptions.length ? (
              <>
                {viewsSelectOptions?.map((view) => {
                  return (
                    <ViewRow
                      key={view.id}
                      view={view}
                      onSelectViewChange={onSelectViewChange}
                      disableSelect={
                        isLoading || sheetViews?.length === 0 || isProcessingRef.current
                      }
                    />
                  );
                })}
              </>
            ) : (
              <div className='flex items-center justify-center w-full h-full p-1'>
                <Button
                  iconName='plus-circle'
                  variant='ghost'
                  onClick={onAddViewButtonClick}
                  dataCy='add-view-modal-button'
                >
                  Add View
                </Button>
              </div>
            )}
          </div>
          <div className='w-4/6 border-l'>
            <ViewToggle
              inputValue={inputValue}
              selectedView={selectedView}
              isSelectedViewEnabled={isSelectedViewEnabled}
              onCopyButtonClick={onCopyButtonClick}
              isToolTipVisible={isToolTipVisible}
              onToggleButtonChange={onToggleButtonChange}
              isDisabled={isToolTipVisible}
            />
          </div>
        </div>
      </div>
      <hr className='my-1 bg-gray' />
      <div
        className={clsx(
          'flex mt-4',
          viewsSelectOptions?.length ? 'justify-between' : 'justify-end'
        )}
      >
        <Button
          iconName='plus-circle'
          variant='ghost'
          onClick={onAddViewButtonClick}
          dataCy='add-view-button'
          className={clsx(!viewsSelectOptions?.length && 'hidden')}
        >
          Add View
        </Button>
        <Button onClick={hideModal} variant='ghost'>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default withModal({ name: MODAL_URL_FOR_BI_AND_SPREADSHEETS, destroyOnHide: true })(
  UrlForBiAndSpreadsheets
);
